import {
	ApiError,
	type MeStore,
	type StoreInvitation,
	type StoreInvitationIssued,
	type StoreOrderScope,
	type StorePasswordMode,
	type StoreRoleTemplate,
	type StoreUser,
	type StoreUserPatch,
} from "@khazzin/api-client";
import type { AccessLevel } from "@khazzin/api-client/permissions";
import { formatDate } from "@khazzin/i18n/dates";
import { SectionAccessGrid, StatusChip, StepUpCodeDialog } from "@khazzin/ui/patterns";
import { EmptyState, ErrorState, Timeline } from "@khazzin/ui/patterns/page";
import {
	Badge,
	Button,
	ConfirmDialog,
	Drawer,
	DropdownMenu,
	Field,
	IconButton,
	Input,
	type MenuItem,
	RadioGroup,
	SegmentedControl,
	Select,
	SkeletonText,
} from "@khazzin/ui/primitives";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { Check, Copy, EllipsisVertical, Mail, UserPlus } from "lucide-react";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf, SECTIONS } from "../../app/shell/sections";
import {
	acceptLink,
	ceilingOf,
	type Grid,
	gridFrom,
	type MemberAction,
	memberActions,
	needsMfa,
	refusedSection,
	SUB_ACCOUNT_TEMPLATES,
	sameGrid,
	seatsUsed,
} from "./team";

/**
 * Team & Access in the Store Portal (EP10-S10, DA-10-12, CR-021).
 *
 * The store's accounts with their template, status, 2FA and last sign-in; "n
 * of max" and Add member / Invite; a member's Edit access drawer with the
 * thirteen-row grid, the cells above the editor's own level disabled; the
 * member actions, Activity, and Pending invitations with Resend / Revoke.
 *
 * It calls the same `/stores/{storeId}/users` endpoints as the Admin Console's
 * Users tab: they already run the portal's section gate and pass the caller as
 * the editor, so the ceiling and the limit are enforced once, in the domain.
 */
export function TeamPage() {
	const { store } = useStoreScope();
	const level = levelOf(store, "team");
	if (store === null || level === "none") {
		return <NotFound />;
	}
	return <Team store={store} mayWrite={level === "write"} />;
}

const teamKey = (storeId: string) => ["team", storeId] as const;
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

type Drawn = { mode: "create" | "invite" } | { mode: "edit"; member: StoreUser };

function Team({ store, mayWrite }: { store: MeStore; mayWrite: boolean }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const storeId = store.storeId;
	const team = useQuery({
		queryKey: [...teamKey(storeId), "users"],
		queryFn: ({ signal }) => api.getStoreTeam(storeId, { signal }),
	});
	const [drawn, setDrawn] = useState<Drawn | null>(null);
	const [activity, setActivity] = useState<StoreUser | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [link, setLink] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const members = team.data?.items ?? [];
	const max = team.data?.maxSubAccounts ?? 1;
	const used = seatsUsed(members);

	function issued(message: string, invitation?: StoreInvitationIssued) {
		setNotice(message);
		setLink(invitation === undefined ? null : acceptLink(invitation.acceptToken));
	}

	return (
		<section aria-labelledby="team-title" className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex flex-col gap-0.5">
					<h1 id="team-title" className="text-[18px] font-bold text-fg">
						{t("portal.shell.sections.team")}
					</h1>
					<p
						data-testid="seats"
						className={
							used >= max ? "text-[12px] font-semibold text-danger-fg" : "text-[12px] text-fg-muted"
						}
					>
						{t("portal.team.seats", { used, max })}
					</p>
				</div>
				{mayWrite && (
					<div className="flex flex-wrap gap-2">
						<Button icon={Mail} variant="secondary" onClick={() => setDrawn({ mode: "invite" })}>
							{t("portal.team.invite")}
						</Button>
						<Button icon={UserPlus} onClick={() => setDrawn({ mode: "create" })}>
							{t("portal.team.add")}
						</Button>
					</div>
				)}
			</div>

			{notice !== null && (
				<div
					role="status"
					className="flex flex-wrap items-center gap-2 rounded-sm border border-border bg-bg-subtle p-3 text-[13px] text-fg"
				>
					<span className="min-w-0 flex-1">
						{notice}
						{link !== null && (
							<span className="block text-[12px] text-fg-muted">{t("portal.team.linkBody")}</span>
						)}
					</span>
					{link !== null && (
						<Button
							size="sm"
							variant="secondary"
							icon={copied ? Check : Copy}
							onClick={() =>
								// ADJ-M03-4: the press says it worked.
								void navigator.clipboard?.writeText(link).then(() => {
									setCopied(true);
									window.setTimeout(() => setCopied(false), 2000);
								})
							}
						>
							{t(copied ? "portal.team.copied" : "portal.team.copyLink")}
						</Button>
					)}
					<Button
						size="sm"
						variant="ghost"
						onClick={() => {
							setNotice(null);
							setLink(null);
						}}
					>
						{t("ui.actions.close")}
					</Button>
				</div>
			)}

			{team.isLoading ? (
				<SkeletonText lines={4} />
			) : team.isError ? (
				<ErrorState error={team.error} onRetry={() => void team.refetch()} t={t} />
			) : (
				<ul
					aria-label={t("portal.team.members")}
					className="m-0 flex list-none flex-col divide-y divide-border rounded-sm border border-border bg-bg-surface p-0"
				>
					{members.map((member) => (
						<li
							key={member.storeUserId}
							data-testid={`member-${member.storeUserId}`}
							className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5"
						>
							<span className="flex min-w-0 flex-1 flex-col">
								<span className="truncate text-[13px] font-medium text-fg">
									{member.displayName}
								</span>
								<span className="truncate text-[11.5px] text-fg-muted">
									{member.email ?? member.phoneE164 ?? "—"} ·{" "}
									{member.lastLoginAt == null
										? t("portal.team.neverSignedIn")
										: t("portal.team.lastLogin", {
												when: formatDate(member.lastLoginAt, { locale, timeZone }),
											})}
								</span>
							</span>
							<Badge>{t(`portal.team.templates.${member.roleTemplate}`)}</Badge>
							<StatusChip domain="stores" status={member.status} size="sm">
								{t(`portal.team.status.${member.status}`)}
							</StatusChip>
							<MfaBadge member={member} />
							{mayWrite ? (
								<Actions
									storeId={storeId}
									member={member}
									callerIsOwner={store.accountKind === "owner"}
									onEdit={() => setDrawn({ mode: "edit", member })}
									onActivity={() => setActivity(member)}
									onDone={(message) => issued(message)}
								/>
							) : (
								<Button size="sm" variant="ghost" onClick={() => setActivity(member)}>
									{t("portal.team.menu.activity")}
								</Button>
							)}
						</li>
					))}
				</ul>
			)}

			{mayWrite && <PendingInvitations storeId={storeId} onIssued={issued} />}

			{drawn !== null && (
				<MemberDrawer
					store={store}
					drawn={drawn}
					members={members}
					max={max}
					onClose={() => setDrawn(null)}
					onDone={(message, invitation) => {
						setDrawn(null);
						issued(message, invitation);
					}}
				/>
			)}
			{activity !== null && (
				<ActivityDrawer storeId={storeId} member={activity} onClose={() => setActivity(null)} />
			)}
		</section>
	);
}

/** The 2FA column: held at Read until 2FA, enrolled, not enrolled, or not asked. */
function MfaBadge({ member }: { member: StoreUser }) {
	const { t } = useLocale();
	if (member.pendingMfaUpgrade === true && member.access.finance !== "write") {
		return (
			<StatusChip tone="warning" size="sm">
				{t("portal.team.financeHeld")}
			</StatusChip>
		);
	}
	if (member.mfaEnrolled === true) {
		return (
			<StatusChip tone="success" size="sm">
				{t("portal.team.mfa.on")}
			</StatusChip>
		);
	}
	return member.mfaEnrolled === false ? (
		<StatusChip tone="neutral" size="sm">
			{t("portal.team.mfa.off")}
		</StatusChip>
	) : null;
}

/** The message a refusal shows: the rule it broke, in the portal's words. */
function refusal(t: ReturnType<typeof useLocale>["t"], error: unknown): string {
	if (!(error instanceof ApiError)) {
		return t("portal.team.failed");
	}
	const section = refusedSection(error);
	switch (error.code) {
		case "above_own_level":
			return t("portal.team.refusal.ceiling", {
				section: t(`portal.shell.sections.${section ?? "team"}`),
			});
		case "mfa_required":
			return t("portal.team.refusal.mfa");
		case "sub_account_limit":
			return t("portal.team.refusal.limit");
		case "version_conflict":
			return t("portal.team.refusal.version");
		default:
			return error.message;
	}
}

// ── add, invite, edit access ───────────────────────────────────────────────

function MemberDrawer({
	store,
	drawn,
	members,
	max,
	onClose,
	onDone,
}: {
	store: MeStore;
	drawn: Drawn;
	members: readonly StoreUser[];
	max: number;
	onClose: () => void;
	onDone: (message: string, invitation?: StoreInvitationIssued) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const storeId = store.storeId;
	const member = drawn.mode === "edit" ? drawn.member : undefined;
	const saved = member === undefined ? null : gridFrom(member.access);
	// The template grids come from the server, never from a copy in this bundle.
	const metadata = useQuery({
		queryKey: ["team", "sections"],
		queryFn: ({ signal }) => api.getStoreSectionMetadata({ signal }),
		staleTime: 60 * 60 * 1000,
	});
	const templateGrid = (template: StoreRoleTemplate): Grid =>
		gridFrom(metadata.data?.templates.find((row) => row.template === template)?.access);

	const [displayName, setDisplayName] = useState(member?.displayName ?? "");
	const [email, setEmail] = useState(member?.email ?? "");
	// Call note 12: only an account nobody has signed in as yet takes a new
	// address; the invitation is withdrawn and sent there instead.
	const emailEditable = member === undefined || member.status === "invited";
	const reinvite = member !== undefined && emailEditable && email.trim() !== (member.email ?? "");
	const [phone, setPhone] = useState("");
	const [template, setTemplate] = useState<StoreRoleTemplate>(
		member?.roleTemplate ?? "store_staff",
	);
	const [scope, setScope] = useState<StoreOrderScope>(member?.orderScope ?? "all");
	const [mode, setMode] = useState<StorePasswordMode>("send_link");
	// A create starts from its template once the server has said what that is.
	const [grid, setGrid] = useState<Grid | null>(saved);
	const shown = grid ?? templateGrid(template);
	const [error, setError] = useState<unknown>(null);

	const save = useMutation({
		mutationFn: async () => {
			const access = shown as Record<string, AccessLevel>;
			if (drawn.mode === "invite") {
				return api.inviteStoreUser(storeId, {
					displayName,
					email,
					...(phone.trim() === "" ? {} : { phoneE164: phone }),
					roleTemplate: template,
					orderScope: scope,
					access,
				});
			}
			if (member === undefined) {
				await api.createStoreSubAccount(storeId, {
					displayName,
					...(email.trim() === "" ? {} : { email }),
					...(phone.trim() === "" ? {} : { phoneE164: phone }),
					roleTemplate: template,
					orderScope: scope,
					passwordMode: mode,
					access,
				});
				return undefined;
			}
			const patch: StoreUserPatch = {};
			if (displayName !== member.displayName) patch.displayName = displayName;
			if (scope !== member.orderScope) patch.orderScope = scope;
			if (template !== member.roleTemplate) patch.roleTemplate = template;
			let current = member;
			if (Object.keys(patch).length > 0) {
				current = await api.updateStoreUser(storeId, member.storeUserId, patch);
			}
			if (!sameGrid(gridFrom(current.access), shown)) {
				// The version read, so a save over somebody else's is refused.
				await api.replaceStoreUserAccess(storeId, member.storeUserId, access, current.version);
			}
			if (reinvite) {
				// After the save, so the account is invited again as just edited.
				await api.changeStoreUserEmail(storeId, member.storeUserId, { email: email.trim() });
			}
			return undefined;
		},
		onSuccess: (invitation) => {
			void queryClient.invalidateQueries({ queryKey: teamKey(storeId) });
			if (drawn.mode === "invite" && invitation !== undefined) {
				onDone(t("portal.team.done.invited", { email }), invitation);
			} else {
				onDone(
					reinvite
						? t("portal.team.done.reinvited", { email: email.trim() })
						: t(member === undefined ? "portal.team.done.created" : "portal.team.done.saved"),
				);
			}
		},
		onError: setError,
	});

	const invalid = refusedSection(error);
	const mfaNote = needsMfa(shown, saved, member) ? (
		<StatusChip tone="warning" size="sm">
			{t(drawn.mode === "invite" ? "portal.team.financeHeld" : "portal.team.mfaRequired")}
		</StatusChip>
	) : undefined;
	const canSubmit =
		!save.isPending &&
		metadata.data !== undefined &&
		displayName.trim() !== "" &&
		(drawn.mode === "invite"
			? email.trim() !== ""
			: member !== undefined || email.trim() !== "" || phone.trim() !== "");

	return (
		<Drawer
			open
			onOpenChange={(next) => {
				if (!next) onClose();
			}}
			size="md"
			title={
				member === undefined
					? t(drawn.mode === "invite" ? "portal.team.inviteTitle" : "portal.team.addTitle")
					: t("portal.team.editTitle", { name: member.displayName })
			}
			description={t("portal.team.drawerBody")}
			closeLabel={t("ui.actions.close")}
			footer={
				<div className="flex w-full flex-wrap items-center justify-between gap-2">
					<span className="text-[12px] text-fg-muted">
						{member === undefined ? t("portal.team.seats", { used: seatsUsed(members), max }) : ""}
					</span>
					<div className="flex gap-2">
						<Button variant="ghost" onClick={onClose}>
							{t("ui.actions.cancel")}
						</Button>
						<Button disabled={!canSubmit} loading={save.isPending} onClick={() => save.mutate()}>
							{member === undefined
								? t(drawn.mode === "invite" ? "portal.team.invite" : "portal.team.add")
								: t("ui.actions.save")}
						</Button>
					</div>
				</div>
			}
		>
			<div className="flex flex-col gap-3">
				<Field label={t("portal.team.name")}>
					{(props) => (
						<Input
							{...props}
							value={displayName}
							onChange={(event) => setDisplayName(event.target.value)}
						/>
					)}
				</Field>
				{member !== undefined && (
					<Field
						label={t("portal.team.email")}
						hint={t(emailEditable ? "portal.team.emailInvited" : "portal.team.emailSignedIn")}
					>
						{(props) => (
							<Input
								{...props}
								type="email"
								value={email}
								readOnly={!emailEditable}
								onChange={(event) => setEmail(event.target.value)}
							/>
						)}
					</Field>
				)}
				{member === undefined && (
					<div className="grid gap-3 sm:grid-cols-2">
						<Field
							label={t("portal.team.email")}
							{...(drawn.mode === "invite" ? { hint: t("portal.team.emailRequired") } : {})}
						>
							{(props) => (
								<Input
									{...props}
									type="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
								/>
							)}
						</Field>
						<Field label={t("portal.team.phone")} hint={t("portal.team.phoneHint")}>
							{(props) => (
								<Input
									{...props}
									type="tel"
									value={phone}
									onChange={(event) => setPhone(event.target.value)}
								/>
							)}
						</Field>
					</div>
				)}
				<Field label={t("portal.team.template")}>
					{(props) => (
						<Select
							{...props}
							value={template}
							onValueChange={(next) => {
								setTemplate(next as StoreRoleTemplate);
								setGrid(templateGrid(next as StoreRoleTemplate));
							}}
							options={SUB_ACCOUNT_TEMPLATES.map((value) => ({
								value,
								label: t(`portal.team.templates.${value}`),
							}))}
						/>
					)}
				</Field>
				<div className="flex flex-col gap-1">
					<span className="text-[12.5px] font-semibold text-fg">{t("portal.team.orderScope")}</span>
					<SegmentedControl<StoreOrderScope>
						aria-label={t("portal.team.orderScope")}
						value={scope}
						onChange={setScope}
						options={[
							{ value: "all", label: t("portal.team.scope.all") },
							{ value: "own_created", label: t("portal.team.scope.own_created") },
						]}
					/>
				</div>
				{drawn.mode === "create" && (
					<div className="flex flex-col gap-1">
						<span id="team-password" className="text-[12.5px] font-semibold text-fg">
							{t("portal.team.passwordMode")}
						</span>
						<RadioGroup
							aria-labelledby="team-password"
							value={mode}
							onValueChange={(next) => setMode(next as StorePasswordMode)}
							options={[
								{ value: "send_link", label: t("portal.team.password.send_link") },
								{ value: "set_password", label: t("portal.team.password.set_password") },
							]}
						/>
					</div>
				)}
				<SectionAccessGrid
					rows={SECTIONS.map((section) => ({
						section,
						label: t(`portal.shell.sections.${section}`),
						sensitive: section === "finance" || section === "team",
					}))}
					value={shown}
					roleDefaults={templateGrid(template)}
					ceiling={ceilingOf(store)}
					label={t("portal.team.grid")}
					t={t}
					onChange={(section, level) => setGrid({ ...shown, [section]: level })}
					onReset={() => setGrid(templateGrid(template))}
					resetLabel={t("portal.team.resetGrid")}
					{...(invalid === null ? {} : { invalid })}
					{...(mfaNote === undefined ? {} : { notes: { finance: mfaNote } })}
				/>
				{error !== null && (
					<p role="alert" className="text-[13px] text-danger-fg">
						{refusal(t, error)}
					</p>
				)}
			</div>
		</Drawer>
	);
}

// ── a member's actions ─────────────────────────────────────────────────────

/** The actions that ask first with a plain confirmation (ADJ-M03-4). */
const ASKED = ["suspend", "resetPassword", "endSessions"] as const;

function Actions({
	storeId,
	member,
	callerIsOwner,
	onEdit,
	onActivity,
	onDone,
}: {
	storeId: string;
	member: StoreUser;
	callerIsOwner: boolean;
	onEdit: () => void;
	onActivity: () => void;
	onDone: (message: string) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const [confirming, setConfirming] = useState<
		"revoke" | "transfer" | (typeof ASKED)[number] | null
	>(null);
	const [stepUp, setStepUp] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);

	const run = useMutation({
		mutationFn: (action: Exclude<MemberAction, "edit" | "activity">): Promise<unknown> => {
			switch (action) {
				case "endSessions":
					return api.endStoreUserSessions(storeId, member.storeUserId);
				case "resetPassword":
					return api.resetStoreUserPassword(storeId, member.storeUserId);
				case "transfer":
					return api.transferStoreOwnership(storeId, member.storeUserId);
				default:
					return api.storeUserLifecycle(storeId, member.storeUserId, action);
			}
		},
		onSuccess: (_, action) => {
			void queryClient.invalidateQueries({ queryKey: teamKey(storeId) });
			if (action === "transfer") {
				// The caller is a manager now: their own grid comes from /me/context.
				void queryClient.invalidateQueries({ queryKey: ["me"] });
			}
			onDone(t(`portal.team.done.${action}`, { name: member.displayName }));
		},
	});

	async function act(action: Exclude<MemberAction, "edit" | "activity">) {
		setFailure(null);
		try {
			await run.mutateAsync(action);
			setConfirming(null);
		} catch (cause) {
			if (cause instanceof ApiError && cause.code === "step_up_required") {
				setConfirming(null);
				setStepUp(true);
				return;
			}
			setFailure(refusal(t, cause));
			if (confirming !== null) {
				throw cause;
			}
		}
	}

	const handlers: Record<MemberAction, () => void> = {
		edit: onEdit,
		activity: onActivity,
		resetPassword: () => setConfirming("resetPassword"),
		endSessions: () => setConfirming("endSessions"),
		suspend: () => setConfirming("suspend"),
		reactivate: () => void act("reactivate"),
		revoke: () => setConfirming("revoke"),
		transfer: () => setConfirming("transfer"),
	};
	const owner = member.accountKind === "owner";
	const items: MenuItem[] = memberActions(member, callerIsOwner).map(({ action, enabled }) => ({
		id: action,
		label:
			owner && !enabled && (action === "revoke" || action === "suspend")
				? `${t(`portal.team.menu.${action}`)} — ${t("portal.team.lastOwner")}`
				: t(`portal.team.menu.${action}`),
		// Nothing else while one action is on its way (ADJ-M03-4).
		disabled: !enabled || run.isPending,
		danger: action === "revoke",
		onSelect: handlers[action],
	}));
	const label = t("portal.team.menu.label", { name: member.displayName });
	const asked = ASKED.find((action) => action === confirming);

	return (
		<>
			<DropdownMenu
				label={label}
				trigger={
					<IconButton
						icon={EllipsisVertical}
						size="sm"
						variant="ghost"
						label={label}
						loading={run.isPending && confirming === null}
					/>
				}
				groups={[items]}
			/>
			{/* ADJ-M03-4: suspending, resetting the password and ending the
			    sessions each lock somebody out, so each asks first. */}
			<ConfirmDialog
				open={asked !== undefined}
				onOpenChange={(open) => !open && setConfirming(null)}
				title={t(`portal.team.confirm.${asked ?? "suspend"}.title`, { name: member.displayName })}
				description={t(`portal.team.confirm.${asked ?? "suspend"}.body`)}
				tone={asked === "suspend" ? "danger" : "default"}
				confirmLabel={t(`portal.team.menu.${asked ?? "suspend"}`)}
				cancelLabel={t("ui.actions.cancel")}
				onConfirm={() => (asked === undefined ? undefined : act(asked))}
			>
				{failure !== null && (
					<p role="alert" className="text-[12px] text-danger-fg">
						{failure}
					</p>
				)}
			</ConfirmDialog>
			{failure !== null && confirming === null && (
				<p role="alert" className="basis-full text-[12px] text-danger-fg">
					{failure}
				</p>
			)}
			<ConfirmDialog
				open={confirming === "revoke"}
				onOpenChange={(open) => !open && setConfirming(null)}
				title={t("portal.team.revokeTitle", { name: member.displayName })}
				description={t("portal.team.revokeBody")}
				tone="danger"
				confirmLabel={t("portal.team.menu.revoke")}
				cancelLabel={t("ui.actions.cancel")}
				onConfirm={() => act("revoke")}
			/>
			<ConfirmDialog
				open={confirming === "transfer"}
				onOpenChange={(open) => !open && setConfirming(null)}
				title={t("portal.team.transferTitle", { name: member.displayName })}
				description={t("portal.team.transferBody")}
				tone="danger"
				confirmLabel={t("portal.team.menu.transfer")}
				cancelLabel={t("ui.actions.cancel")}
				requireTypedText={member.displayName}
				typedTextLabel={t("portal.team.typeName", { name: member.displayName })}
				onConfirm={() => act("transfer")}
			>
				{failure !== null && (
					<p role="alert" className="text-[12px] text-danger-fg">
						{failure}
					</p>
				)}
			</ConfirmDialog>
			<StepUpDialog
				open={stepUp}
				onOpenChange={setStepUp}
				onConfirmed={() => void act("transfer")}
			/>
		</>
	);
}

/**
 * "Confirm it's you" before a transfer (EP10-S10): the code from the owner's
 * authenticator app, verified by the server with the identity provider
 * (ADJ-M03-10). Somebody with no authenticator is sent to sign in, where
 * AuthKit enrols one.
 */
function StepUpDialog({
	open,
	onOpenChange,
	onConfirmed,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirmed: () => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const { context } = useStoreScope();
	const { signIn } = useAuth();
	return (
		<StepUpCodeDialog
			open={open}
			onOpenChange={onOpenChange}
			confirm={(code) => api.confirmStepUp(context?.session.sid ?? "", code)}
			onConfirmed={onConfirmed}
			onEnrol={() => void signIn({ state: { returnTo: window.location.pathname } })}
			t={t}
		/>
	);
}

/** One account's trail, newest first: what was done to it and what it did here. */
function ActivityDrawer({
	storeId,
	member,
	onClose,
}: {
	storeId: string;
	member: StoreUser;
	onClose: () => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const trail = useQuery({
		queryKey: [...teamKey(storeId), "activity", member.storeUserId],
		queryFn: () => api.getStoreUserActivity(storeId, member.storeUserId),
	});
	const items = trail.data?.items ?? [];
	return (
		<Drawer
			open
			onOpenChange={(next) => {
				if (!next) onClose();
			}}
			size="md"
			title={t("portal.team.activityTitle", { name: member.displayName })}
			closeLabel={t("ui.actions.close")}
		>
			{trail.isLoading ? (
				<SkeletonText lines={4} />
			) : trail.isError ? (
				<ErrorState error={trail.error} onRetry={() => void trail.refetch()} t={t} />
			) : items.length === 0 ? (
				<EmptyState title={t("portal.team.activityEmpty")} />
			) : (
				<Timeline
					items={items.map((event) => ({
						id: event.eventId,
						title: event.summary ?? event.action,
						meta: [
							formatDate(event.occurredAt, { locale, timeZone }),
							...event.changes.map(
								(c) => `${c.field}: ${String(c.old ?? "—")} → ${String(c.new ?? "—")}`,
							),
						].join(" · "),
						state: "done" as const,
					}))}
				/>
			)}
		</Drawer>
	);
}

// ── pending invitations ────────────────────────────────────────────────────

function PendingInvitations({
	storeId,
	onIssued,
}: {
	storeId: string;
	onIssued: (message: string, invitation?: StoreInvitationIssued) => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const invitations = useQuery({
		queryKey: [...teamKey(storeId), "invitations"],
		queryFn: ({ signal }) => api.listStoreInvitations(storeId, { signal }),
	});
	const run = useMutation({
		mutationFn: ({
			row,
			kind,
		}: {
			row: StoreInvitation;
			kind: "resend" | "revoke";
		}): Promise<StoreInvitationIssued | StoreUser> =>
			kind === "resend"
				? api.resendStoreInvitation(storeId, row.invitationId)
				: api.revokeStoreInvitation(storeId, row.invitationId),
		onSuccess: (result, { row, kind }) => {
			void queryClient.invalidateQueries({ queryKey: teamKey(storeId) });
			const email = row.member.email ?? row.member.displayName;
			if (kind === "resend") {
				onIssued(t("portal.team.done.resent", { email }), result as StoreInvitationIssued);
			} else {
				onIssued(t("portal.team.done.withdrawn", { email }));
			}
		},
		onError: (cause) => onIssued(refusal(t, cause)),
	});
	const rows = invitations.data ?? [];
	if (rows.length === 0) {
		return null;
	}
	return (
		<section aria-labelledby="team-invitations" className="flex flex-col gap-2">
			<h2 id="team-invitations" className="text-[14px] font-bold text-fg">
				{t("portal.team.invitations")}
			</h2>
			<ul className="m-0 flex list-none flex-col divide-y divide-border rounded-sm border border-border bg-bg-surface p-0">
				{rows.map((row) => (
					<li
						key={row.invitationId}
						data-testid={`invitation-${row.invitationId}`}
						className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5"
					>
						<span className="flex min-w-0 flex-1 flex-col">
							<span className="truncate text-[13px] font-medium text-fg">
								{row.member.email ?? row.member.displayName}
							</span>
							<span className="text-[11.5px] text-fg-muted">
								{t("portal.team.sentExpires", {
									sent: formatDate(row.createdAt, { locale, timeZone }),
									expires: formatDate(row.expiresAt, { locale, timeZone }),
								})}
							</span>
						</span>
						<Badge>{t(`portal.team.templates.${row.member.roleTemplate}`)}</Badge>
						<StatusChip tone={row.status === "expired" ? "neutral" : "warning"} size="sm">
							{t(`portal.team.invitationStatus.${row.status}`)}
						</StatusChip>
						<span className="flex gap-1.5">
							<Button
								size="sm"
								variant="secondary"
								disabled={run.isPending}
								onClick={() => run.mutate({ row, kind: "resend" })}
							>
								{t("portal.team.resend")}
							</Button>
							<Button
								size="sm"
								variant="ghost"
								disabled={run.isPending || row.member.accountKind === "owner"}
								onClick={() => run.mutate({ row, kind: "revoke" })}
							>
								{t("portal.team.withdraw")}
							</Button>
						</span>
					</li>
				))}
			</ul>
		</section>
	);
}
